import { z } from 'zod';
const patientValidationSchema = z.intersection(z.object({
    firstName: z.string({ message: 'First name is required' }).min(1, 'First name is required'),
    lastName: z.string({ message: 'Last name is required' }).min(1, 'Last name is required'),
    gender: z.string({ message: 'Gender is required' }).min(1, 'Gender is required'),
    dob: z.string({ message: 'Date of birth is required' }).date('Date of birth is required'),
}), z.union([
    z.object({
        ssn: z
            .string({ message: 'Social security number required' })
            .length(9, 'SSN must be 9 digits')
            .regex(/^\d+$/, 'Only numeric characters are allowed'),
        skipSsn: z.boolean().optional().nullish(),
    }),
    z.object({
        ssn: z.string().optional().nullish(),
        skipSsn: z.literal(true, { message: 'Social security number required' }),
    }),
]));
const insuranceValidationSchema = z
    .object({
    insuranceType: z.string().optional().nullish(),
    eligibility: z.array(z.object({
        result: z.string().optional().nullish(),
    })),
})
    .optional();
const demographicsValidationSchema = z.object({
    languageId: z.string().nullish(),
    religionId: z.string().nullish(),
    ethnicity: z.array(z.string()).nullish(),
    race: z.array(z.string()).nullish(),
    maritalStatusId: z.string().nullish(),
    requiresTranslator: z.boolean().nullish(),
});
const contactsValidationSchema = z.object({
    patientId: z.string().optional(),
    pointOfContact: z.string({ message: 'Point of contact is required' }).min(1, 'required'),
    phones: z.array(z.object({
        number: z.string().min(10, 'Please enter a valid phone number'),
        primary: z.boolean(),
    })),
    email: z.string().email('Should be a valid email').or(z.string().nullish()),
    addresses: z
        .array(z.object({
        id: z.string().optional(),
        address: z.string({ message: 'Address is required' }).min(1, 'Address is required'),
        city: z.string({ message: 'City is required' }).min(1, 'City is required'),
        state: z.string().min(2, 'State is required').max(2, 'State is required'),
        zipcode: z
            .string()
            .regex(/^\d{5}(-?\d{4})?$/, 'Invalid zip code')
            .max(10),
        homeType: z.string().optional().nullish(),
        accessCode: z.string().optional().nullish(),
        callBell: z.enum(['yes', 'no']).optional().nullish(),
        notes: z.string().max(250).optional().nullish(),
        primary: z.boolean(),
        livingWith: z.array(z.string()).nullish(),
        livingWithOther: z.string().optional().nullish(),
    }))
        .min(1),
});
const supportValidationSchema = z.object({
    powerOfAttorney: z.enum(['yes', 'no']).optional().nullish(),
});
const referralValidationSchema = z.intersection(z.object({
    receivedBy: z.string(),
    referralSource: z.string(),
    admitSource: z.string(),
    careType: z.array(z.string()).min(1),
}), z.union([
    z.object({
        referralDate: z.string().date(),
        unknownReferralDate: z.boolean().optional(),
    }),
    z.object({
        referralDate: z.string().date().optional().nullish(),
        unknownReferralDate: z.boolean(),
    }),
]), z.union([
    z.object({
        startOfCareDate: z.string().date(),
        noStartOfCareDate: z.boolean().optional(),
    }),
    z.object({
        startOfCareDate: z.string().date().optional().nullish(),
        noStartOfCareDate: z.boolean(),
    }),
]));
const providersValidationSchema = z.object({
    physicians: z
        .array(z.object({
        physician: z.object({
            id: z.string(),
            label: z.string(),
        }),
        roles: z.array(z.string()).min(1, 'requires at least one role'),
        primary: z.boolean().nullable(),
        overseePlanOfCare: z.boolean().nullable(),
    }))
        .min(1)
        .superRefine((data, ctx) => {
        for (const [index, value] of data.entries()) {
            const hasDuplicate = data.filter((d) => d.physician.id === value.physician.id).length > 1;
            if (hasDuplicate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Duplicate physician',
                    path: [index, 'label'],
                });
            }
        }
    }),
    pharmacies: z
        .array(z.object({
        pharmacy: z
            .object({
            id: z.string(),
            label: z.string(),
        })
            .required(),
        primary: z.boolean().nullable(),
    }))
        .superRefine((data, ctx) => {
        for (const [index, value] of data.entries()) {
            const hasDuplicate = data.filter((d) => d.pharmacy.id === value.pharmacy.id).length > 1;
            if (hasDuplicate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Duplicate pharmacy',
                    path: [index, 'label'],
                });
            }
        }
    }),
});
const diagnosesValidationSchema = z.object({
    code: z.string(),
    description: z.string(),
    primary: z.boolean().nullish().optional(),
    onset: z.string().date().optional().nullish(),
});
const risksAllergiesValidationSchema = z.object({
    risks: z
        .array(z.object({
        risk: z.string().nullish(),
        severity: z.string().nullish(),
    }))
        .nullish(),
    allergies: z
        .array(z.object({
        allergy: z.string().nullish(),
        severity: z.string().nullish(),
    }))
        .nullish(),
});
export const intakeValidationSchema = z.object({
    patient: patientValidationSchema,
    insurance: z.array(insuranceValidationSchema).superRefine((values, ctx) => {
        const validInsurance = values.some((insurance) => {
            return ['self-pay', 'no-pay'].includes(insurance?.insuranceType || '') || insurance?.eligibility?.[0]?.result === 'approved';
        });
        if (!validInsurance) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'No eligible insurance',
            });
        }
    }),
    demographics: demographicsValidationSchema,
    contacts: contactsValidationSchema,
    support: supportValidationSchema,
    referral: referralValidationSchema,
    providers: providersValidationSchema,
    diagnoses: z.array(diagnosesValidationSchema).min(1),
    risksAllergies: risksAllergiesValidationSchema,
});
